<script>
  import Icon from "$lib/components/Icon.svelte";
  import { isExternalUrl } from "$lib/utilities/url-helpers";
  // eslint-disable-next-line  @ObamaFoundation/of-svelte/no-stores
  import { page } from "$app/stores";
  import { createEventDispatcher, onMount } from "svelte";

  let clazz = "";
  export let id;
  /** @type { string } */
  export { clazz as class };
  /** @type {"primary" | "secondary" | "tertiary" | "ghost" | "donate" | "productMenu"} */
  export let variant = "primary";
  /** @type {"base" | "small" | "large" | "header"} */
  export let size = "base";
  /** @type { "submit" | "reset"} */
  export let type = null;
  /** @type { boolean } */
  export let disabled = false;
  /** @type { string } */
  export let testid = null;
  /** @type { { leading: boolean, trailing: boolean, type: string } } */
  export let icon = null;
  export let url;

  const dispatch = createEventDispatcher();
  const typeProps = type ? { type } : {};

  const VARIANT_OPTIONS = ["primary", "secondary", "tertiary", "ghost", "donate", "productMenu"];
  if (!VARIANT_OPTIONS.includes(variant)) {
    throw new Error("invalid button variant.");
  }

  onMount(() => {
    // This is just a quick bandaid as this exception currently isn't managed by Content. -Christina
    if (variant === "donate") {
      if ($page.url?.pathname.includes("monthly-sustainer")) {
        url = "?form=SWXXXXFSUS0";
      }
      if ($page.url?.pathname.includes("givemonthly")) {
        url = "?form=W24XXMCDM0";
      }
    }
  });

  function getVariantClasses() {
    const variantClassMap = {
      primary: `btn btn-primary ripple`,
      secondary: `btn btn-secondary ripple`,
      tertiary: `btn btn-tertiary ripple`,
      ghost: `btn btn-ghost ripple`,
      donate: `btn btn-donate ripple`,
      productMenu: `menu-trigger-link px-24 py-16 lg:px-16 lg:py-8 menu-trigger justify-between`
    };
    return variantClassMap[variant];
  }

  function getSizeClasses() {
    const sizeClassMap = {
      small: "py-6",
      base: "py-10",
      large: "py-16",
      header: "py-10 lg:py-16",
      headerActionButton: "py-10 lg:py-16 sm:px-16 lg:px-24 sm:py-6"
    };
    return sizeClassMap[size];
  }

  function getRippleColors() {
    const rippleColorMap = {
      primary: {
        start: "var(--color-indigo-700)",
        end: "var(--color-indigo-800)"
      },
      secondary: {
        start: "var(--color-cyan-600)",
        end: "var(--color-cyan-900)"
      },
      tertiary: {
        start: "var(--color-indigo-50)",
        end: "var(--theme-highlight)"
      },
      ghost: {
        start: "var(--color-indigo-50)",
        end: "var(--theme-highlight)"
      },
      donate: {
        start: "var(--color-marigold-300)",
        end: "var(--color-marigold-400)"
      },
      productMenu: {}
    };

    return Object.entries(rippleColorMap[variant])
      .map(([key, val]) => `--ripple-${key}-color: ${val}`)
      .join(";");
  }

  $: isExternal = isExternalUrl(url, $page.url?.origin);
  const displayIconOnlyOnMobile = variant === "productMenu" ? "lg:hidden" : "";
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<svelte:element
  this={url ? "a" : "button"}
  {...typeProps}
  href={url ? url : ""}
  data-sveltekit-reload
  {disabled}
  on:click
  on:click={() => {
    dispatch("urlDispatch", {
      url
    });
  }}
  class="{getVariantClasses()} {getSizeClasses()} {clazz}"
  style={!disabled ? getRippleColors() : null}
  data-testid={testid}
  {...$$restProps}
  target={isExternal ? "_blank" : null}
  rel={isExternal ? "noopener noreferrer" : null}
  {id}
>
  {#if !!icon?.type && icon?.leading}
    <Icon type={icon.type} size={icon.size} class="mr-8 {displayIconOnlyOnMobile}" />
  {/if}
  <slot />
  {#if !!icon?.type && icon?.trailing && (!isExternal || variant == "donate")}
    <Icon type={icon.type} size={icon.size} class="ml-8 {displayIconOnlyOnMobile}" />
  {/if}
  {#if url && isExternal && variant !== "donate"}
    <Icon type="external" size="md" class="!inline ml-8 align-top" />
  {/if}
</svelte:element>

<style lang="postcss">
  .ripple {
    background-position: center;
    transition: background 0.5s;

    &:hover {
      background-image: radial-gradient(circle, transparent 1%, var(--ripple-start-color) 1%);
      background-size: 15000%;
    }

    &:active {
      background-color: var(--ripple-end-color);
      background-size: 100%;
      transition: background 0s;
    }
  }
</style>
